import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Card, Row, Col, Typography, Empty, Skeleton } from "antd";

import { AppState } from "store";
import { Table } from "components/base";
import { ROUTES } from "utils/constants";
import { useHistory } from "react-router-dom";

import { parseZoneTable } from "utils/parsers";
import { initializeZones } from "store/actions";
import { TableProps } from "antd/lib/table";
import useDebounce from "utils/hooks";
import { ActionMenu } from "./cell-renders";

import "styles/components/tab-content/zones-content/zones-content.scss";

const ActionMenuAny: any = ActionMenu;

const { useBreakpoint } = Grid;
const { Text, Title } = Typography;

// Desktop/Tablet Table Columns
const getColumns = (
  userType: number,
  isTablet: boolean
): TableProps<any>["columns"] => {
  const columns: TableProps<any>["columns"] = [
    {
      title: "Name",
      dataIndex: "name",
      width: isTablet ? 150 : 210,
      ellipsis: true,
    },
    {
      title: "Actions",
      dataIndex: "action",
      width: isTablet ? 80 : 120,
      align: "center",
      render: (_, record) => <ActionMenuAny record={record} />,
    },
  ];

  return columns;
};

// Zone Card Component for Mobile
interface ZoneCardProps {
  zone: any;
}

const ZoneCard = ({ zone }: ZoneCardProps) => {
  const history = useHistory();

  const handleCardClick = () => {
    // Navigate to zone details if needed
    // history.push(`${ROUTES.zones}/${zone.id}`);
  };

  return (
    <Card className="zone-card" size="small" onClick={handleCardClick}>
      {/* Card Header */}
      <div className="zone-card-header">
        <div className="zone-info">
          <Title level={5} className="zone-name">
            {zone.name || "Unnamed Zone"}
          </Title>
          {zone.description && (
            <Text type="secondary" className="zone-description">
              {zone.description}
            </Text>
          )}
        </div>
        <div className="zone-actions" onClick={(e) => e.stopPropagation()}>
          <ActionMenuAny record={zone} />
        </div>
      </div>

      {/* Card Body - Additional Info */}
      {(zone.area || zone.type || zone.status) && (
        <div className="zone-card-body">
          {zone.type && (
            <div className="zone-detail-item">
              <Text className="detail-label">Type:</Text>
              <Text className="detail-value">{zone.type}</Text>
            </div>
          )}
          {zone.area && (
            <div className="zone-detail-item">
              <Text className="detail-label">Area:</Text>
              <Text className="detail-value">{zone.area}</Text>
            </div>
          )}
          {zone.status && (
            <div className="zone-detail-item">
              <Text className="detail-label">Status:</Text>
              <Text className="detail-value">{zone.status}</Text>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// Loading Skeleton for Mobile
const MobileSkeleton = () => (
  <Row gutter={[16, 16]}>
    {[1, 2, 3, 4].map((item) => (
      <Col xs={24} sm={12} key={item}>
        <Card className="zone-card">
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
      </Col>
    ))}
  </Row>
);

// Mobile List View
interface MobileViewProps {
  data: any[];
  loading?: boolean;
}

const MobileView = ({ data, loading }: MobileViewProps) => {
  if (loading) {
    return <MobileSkeleton />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <Empty
          description="No zones found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="mobile-zones-list">
      <Row gutter={[12, 12]}>
        {data.map((zone, index) => (
          <Col xs={24} sm={12} key={zone.id || zone.key || index}>
            <ZoneCard zone={zone} />
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Main Table View Component
export default function TableView() {
  const dispatch = useDispatch();
  const history = useHistory();
  const screens = useBreakpoint();

  // Determine screen size
  const isMobile = !screens.md;
  const isTablet = !!screens.md && !screens.lg;

  const state = useSelector<AppState, AppState>((state) => state);

  const {
    zones: { zones, search },
    common: { userType = 1 },
  } = state;

  // 'loading' may not be declared on ZonesState; read it safely at runtime with a fallback
  const loading = (state as any).zones?.loading ?? false;

  const data = useMemo(
    () => parseZoneTable(search, zones),
    [zones, search]
  );

  const debounceSearchString = useDebounce(search, 1000);

  useEffect(() => {
    dispatch(initializeZones());
  }, [dispatch, debounceSearchString]);

  // Get columns based on screen size
  const columns = useMemo(
    () => getColumns(userType, isTablet),
    [userType, isTablet]
  );

  // Table scroll configuration
  const getScrollConfig = () => {
    if (isTablet) {
      return { y: "65vh", x: 300 };
    }
    return { y: "70vh" };
  };

  return (
    <div className="zones-table-container">
      {isMobile ? (
        <MobileView data={data} loading={loading} />
      ) : (
        <Table
          className="zones-table"
          columns={columns}
          dataSource={data}
          onRow={(record) => ({
            // onClick: () => handleRowClick(record.raw),
          })}
       //   scroll={getScrollConfig()}
          pagination={false}
          size={isTablet ? "small" : "middle"}
          rowKey={(record) => record.id || record.key}
        />
      )}
    </div>
  );
}